import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Dialog } from "@mui/material";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState, Spinner, StatTile } from "../../components/UI/States";
import { BackLink, BranchSelect, Notice, SearchField } from "../../components/Fleet/FleetParts";
import { formatDate, relativeDay } from "../../components/Fleet/fleetFormat";
import { fetchExtinguisherInspections, fetchExtinguisherPhotoUrl, fetchExtinguishers } from "./safetyApi";
import { Chip, DialogHeader, matchesQuery, pickBranch, plural, sameBranch, useUrlFilters } from "./SafetyParts";
import { SAFETY_PAGES } from "./safetyPages";
import { ReportActions, reportScope } from "./SafetyReport";

// Fire extinguishers and their monthly inspections — read-only here. Units
// are added and inspected in the mobile app (Safety & Training → Fire
// Extinguishers; managers also use Safety Manager → Fire Extinguishers).

const PAGE = SAFETY_PAGES.extinguishers;

const STATUSES = {
  attention: { label: "Needs attention", hint: "Due or failed", tone: "amber", test: (e) => e.due || e.failed },
  failed: { label: "Failed last check", hint: "An item didn't pass", tone: "rose", test: (e) => e.failed },
  due: { label: "Due", hint: "Inspection is due", tone: "amber", test: (e) => e.due },
  never: { label: "Never inspected", hint: "No inspection yet", tone: "rose", test: (e) => !e.lastInspectedAt },
  ok: { label: "Up to date", hint: "Inspected and passing", tone: "emerald", test: (e) => !e.due && !e.failed },
  all: { label: "All", hint: "Every extinguisher", tone: "default", test: () => true },
};

export const ExtinguisherChips = ({ unit }) => (
  <span className="flex flex-wrap gap-1">
    {unit.failed && <Chip tone="rose">Failed last check</Chip>}
    {!unit.lastInspectedAt ? (
      <Chip tone="rose">Never inspected</Chip>
    ) : unit.due ? (
      <Chip tone="amber">Due</Chip>
    ) : (
      !unit.failed && <Chip tone="emerald">Up to date</Chip>
    )}
  </span>
);

const ExtinguisherDialog = ({ unit, userId, onClose }) => {
  const [inspections, setInspections] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setInspections(null);
    setPhoto(null);
    setError(null);
    if (!unit) return undefined;
    let active = true;
    let photoUrl = null;
    fetchExtinguisherInspections(userId, unit.id)
      .then((data) => active && setInspections(data.inspections))
      .catch((e) => active && setError(e.message));
    if (unit.hasImage) {
      setPhoto("loading");
      fetchExtinguisherPhotoUrl(userId, unit.id)
        .catch(() => null)
        .then((url) => {
          photoUrl = url;
          if (active) setPhoto(url);
          else if (url) URL.revokeObjectURL(url);
        });
    }
    return () => {
      active = false;
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
  }, [unit, userId]);

  return (
    <Dialog open={Boolean(unit)} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { m: 2 } }} aria-labelledby="extinguisher-title">
      {unit && (
        <div className="p-5 sm:p-6">
          <DialogHeader
            id="extinguisher-title"
            icon={PAGE.icon}
            tone={PAGE.tone}
            title={unit.assetCode || "Fire extinguisher"}
            subtitle={unit.branch}
            onClose={onClose}
          />
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ExtinguisherChips unit={unit} />
            <span className="app-muted text-sm">
              {unit.lastInspectedAt ? `Last inspected ${formatDate(unit.lastInspectedAt)}` : "Never inspected"}
            </span>
          </div>
          {unit.description && <p className="mt-3 text-sm">{unit.description}</p>}
          {unit.lat !== null && unit.lng !== null && (
            <a
              href={`https://www.google.com/maps?q=${unit.lat},${unit.lng}`}
              target="_blank"
              rel="noreferrer"
              className="app-link mt-2 inline-flex items-center gap-1 text-sm font-semibold"
            >
              <PlaceRoundedIcon sx={{ fontSize: 18 }} /> Open the pin in Google Maps
            </a>
          )}

          {photo && (
            <div className="app-light-surface mt-4 flex min-h-[8rem] items-center justify-center overflow-hidden rounded-xl">
              {photo === "loading" ? (
                <span className="app-muted text-xs">Loading photo…</span>
              ) : (
                <img src={photo} alt={`Where ${unit.assetCode} is mounted`} className="max-h-72 w-full object-contain" />
              )}
            </div>
          )}

          <p className="app-eyebrow mb-2 mt-5">Inspection history</p>
          {error && <Notice tone="rose">{error}</Notice>}
          {!inspections && !error && (
            <div className="flex items-center gap-2 text-sm">
              <Spinner size={18} /> <span className="app-muted">Loading inspections…</span>
            </div>
          )}
          {inspections?.length === 0 && <p className="app-muted text-sm">No inspections yet.</p>}
          {inspections?.length > 0 && (
            <ul className="space-y-2">
              {inspections.map((i) => (
                <li key={i.id} className="app-surface rounded-xl px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold">{formatDate(i.inspectedAt)}</span>
                    <Chip tone={i.passed ? "emerald" : "rose"}>{i.passed ? "Passed" : "Failed"}</Chip>
                  </div>
                  {i.failedItems.length > 0 && (
                    <p className="text-rose-700 dark:text-rose-300">{i.failedItems.join(" · ")}</p>
                  )}
                  {i.notes && <p className="break-words">“{i.notes}”</p>}
                  <p className="app-muted text-xs">{i.inspectorName || "Inspector"}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
};

const FireExtinguishers = () => {
  const user = useSelector(selectUser);
  const filters = useUrlFilters(Object.keys(STATUSES), "all");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetchExtinguishers(user.id)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [user?.id]);

  const units = data?.extinguishers;
  const branches = useMemo(
    () => [...new Set((units || []).map((u) => u.branch))].sort((a, b) => a.localeCompare(b)),
    [units]
  );
  const inBranch = (units || []).filter((u) => !filters.branch || sameBranch(u.branch, filters.branch));
  const visible = inBranch
    .filter(STATUSES[filters.status].test)
    .filter((u) => matchesQuery(filters.query, u.assetCode, u.description, u.branch))
    .sort(
      (a, b) =>
        a.branch.localeCompare(b.branch) || a.assetCode.localeCompare(b.assetCode, undefined, { numeric: true })
    );

  return (
    <PageShell size="xl">
      <ExtinguisherDialog unit={open} userId={user?.id} onClose={() => setOpen(null)} />
      <BackLink to="/safety/overview">Safety Overview</BackLink>
      <PageHeader
        icon={PAGE.icon}
        tone={PAGE.tone}
        eyebrow="Safety"
        title={PAGE.name}
        description={`Every extinguisher and its monthly inspection. A unit is due ${
          data?.dueAfterDays || 30
        } days after its last inspection, or right away if it's never been inspected.`}
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!units && !error && <LoadingState label="Loading extinguishers…" />}

      {units && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {Object.entries(STATUSES).map(([key, s]) => (
              <StatTile
                key={key}
                label={s.label}
                value={inBranch.filter(s.test).length}
                hint={s.hint}
                tone={s.tone}
                active={filters.status === key}
                onClick={() => filters.set("status", key)}
              />
            ))}
          </div>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <SearchField
              value={filters.query}
              onChange={(v) => filters.set("q", v)}
              placeholder="Search asset code or location…"
            />
            <BranchSelect
              value={pickBranch(branches, filters.branch)}
              onChange={(v) => filters.set("branch", v)}
              branches={branches}
            />
            <span className="app-muted text-sm tabular-nums sm:ml-auto">{plural(visible.length, "extinguisher")}</span>
            <ReportActions
              kind="extinguishers"
              userId={user?.id}
              tone={PAGE.tone}
              disabled={!visible.length}
              selection={{
                ids: visible.map((u) => u.id),
                branch: pickBranch(branches, filters.branch),
                scope: reportScope({
                  branch: pickBranch(branches, filters.branch),
                  status: filters.status === "all" ? null : STATUSES[filters.status].label,
                  query: filters.query,
                }),
              }}
            />
          </div>

          <div className="app-card overflow-hidden rounded-2xl border">
            {visible.length === 0 ? (
              <EmptyState
                compact
                icon={PAGE.icon}
                title={units.length ? "No extinguishers match these filters." : "No extinguishers added yet."}
                description="Extinguishers are added from the Big C mobile app (Safety Manager → Fire Extinguishers)."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th className="!pl-4 sm:!pl-5">Asset code</th>
                      <th>Branch</th>
                      <th>Location</th>
                      <th>Last inspected</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((unit) => (
                      <tr key={unit.id} className="cursor-pointer" onClick={() => setOpen(unit)}>
                        <td className="!pl-4 sm:!pl-5">
                          <button
                            type="button"
                            className="whitespace-nowrap font-semibold tabular-nums hover:text-[color:var(--app-accent)]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(unit);
                            }}
                          >
                            {unit.assetCode || "—"}
                          </button>
                        </td>
                        <td className="whitespace-nowrap">{unit.branch}</td>
                        <td className="app-muted max-w-[16rem] truncate">{unit.description || "—"}</td>
                        <td className="whitespace-nowrap tabular-nums">{relativeDay(unit.lastInspectedAt)}</td>
                        <td>
                          <ExtinguisherChips unit={unit} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </PageShell>
  );
};

export default FireExtinguishers;
